import {
  DEFAULT_EINVOICE_ADDRESS_SCHEME_TYPE,
  EInvoiceAddressSchemeType,
  EInvoicingAddress,
  EntityType,
  getDefaultAddressSchemeTypeByCountry,
} from '@components'

import { ReactElement, useCallback } from 'react'

import { useSegment } from '../../../../../../../../hooks'
import { useDebouncedCallback } from '../../../../../../../../hooks/useDebouncedCallback'
import { useCustomerForm } from '../../../../contexts/customerFormContext'
import { useUpdateDependentCustomerFormValues } from '../../../../hooks/useUpdateDependentCustomerFormValues'

export const CustomerEInvoicingAddress = (): ReactElement => {
  const { track } = useSegment()
  const { handleVATNumberSync } = useUpdateDependentCustomerFormValues()
  const { customerForm } = useCustomerForm()
  const {
    formState: { errors },
    getValues,
    register,
    trigger,
    setValue,
    watch,
  } = customerForm

  const eInvoiceAddressFormField = register('eInvoiceAddress.address')
  const [countryId, entityType, address, schemeType, autoCompletedFields] = watch([
    'address.country',
    'entityType',
    'eInvoiceAddress.address',
    'eInvoiceAddress.scheme',
    'autoCompletedFields',
  ])

  const eInvoiceAddressInputErrorMessage = errors.eInvoiceAddress?.address?.message
  const isEInvoiceAddressAutoCompleted = Boolean(autoCompletedFields?.['eInvoiceAddress.address'])

  const trackEInvoiceAddressInputChange = useDebouncedCallback(() => {
    track('XXX - Invoicing Core - Company modal - E-invoicing - E-invoicing address typed')
  })

  const handleEInvoiceAddressInputChange = useCallback(
    (newAddress?: string) => {
      trackEInvoiceAddressInputChange()
      setValue('eInvoiceAddress.address', newAddress)

      if (isEInvoiceAddressAutoCompleted) {
        setValue('autoCompletedFields', { ...autoCompletedFields, 'eInvoiceAddress.address': false })
      }

      const schemeType = getValues('eInvoiceAddress.scheme')

      if (schemeType === DEFAULT_EINVOICE_ADDRESS_SCHEME_TYPE && newAddress !== undefined) {
        handleVATNumberSync(newAddress)
      }

      if (eInvoiceAddressInputErrorMessage) {
        trigger('eInvoiceAddress.address')
      }
    },
    [
      autoCompletedFields,
      eInvoiceAddressInputErrorMessage,
      getValues,
      handleVATNumberSync,
      isEInvoiceAddressAutoCompleted,
      setValue,
      trackEInvoiceAddressInputChange,
      trigger,
    ],
  )

  const handleEInvoiceAddressSchemeTypeChange = useCallback(
    (newSchemeType?: EInvoiceAddressSchemeType) => {
      track('XXX - Invoicing Core - Company modal - E-invoicing - ID type selected', { selectedIdType: newSchemeType })
      setValue('eInvoiceAddress.scheme', newSchemeType)

      const newAddress = newSchemeType === DEFAULT_EINVOICE_ADDRESS_SCHEME_TYPE ? getValues('vatNumber') : undefined
      handleEInvoiceAddressInputChange(newAddress)
    },
    [getValues, handleEInvoiceAddressInputChange, setValue, track],
  )

  const handleEInvoiceAddressEntityTypeChange = useCallback(
    (newEntityType?: EntityType) => {
      setValue('entityType', newEntityType)

      const isPublicEntity = newEntityType === EntityType.Public
      const defaultSchemeType = getDefaultAddressSchemeTypeByCountry(isPublicEntity, countryId)
      handleEInvoiceAddressSchemeTypeChange(defaultSchemeType)
    },
    [countryId, handleEInvoiceAddressSchemeTypeChange, setValue],
  )

  return (
    <EInvoicingAddress
      address={address}
      autoCompleted={isEInvoiceAddressAutoCompleted}
      countryId={countryId}
      eInvoiceAddressFormField={eInvoiceAddressFormField}
      eInvoiceAddressInputErrorMessage={eInvoiceAddressInputErrorMessage}
      entityType={entityType}
      onEInvoiceAddressEntityTypeChange={handleEInvoiceAddressEntityTypeChange}
      onEInvoiceAddressInputChange={handleEInvoiceAddressInputChange}
      onEInvoiceAddressSchemeTypeChange={handleEInvoiceAddressSchemeTypeChange}
      schemeType={schemeType || ''}
      size="sm"
    />
  )
}
